import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment-timezone";
import { faChevronDown, faChevronUp, faClock } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ColumnToggle from "../../../../../components/NTable/Actions/ColumnToggle";
import Item from "./Item";
import { useNewTable } from "../../../../../components/NTable/hook";
import { useGroups } from "../../../../Settings/General/OrganisationLevelGroups/context";
import { useCustomFields } from "../../../../Settings/CustomFields/context";
import { useCompanyCurrency, useCompanyDateFormat } from "../../../../../utils/hooks/company";
import { getPrimaryActiveResource } from "../../../../../utils/helpers/user";
import { renderDurationAsFormat } from "../../../../../utils/helpers/date";
import { currencyFormatter } from "../../../../../utils/helpers/currencyFormatter";

function RetroContent({ user, item, columns, expandedRow, setExpandedRow, hideExpand }) {
    const { t } = useTranslation();
    const currency = useCompanyCurrency();
    const dateFormat = useCompanyDateFormat();
    const primaryJob = useMemo(() => {
        return getPrimaryActiveResource(user?.jobs)
    }, [user?.jobs]);
    const [expanded, setExpanded] = useState({});
    const { groups } = useGroups();
    const { timesheetCustomFields } = useCustomFields();

    const groupedByDate = useMemo(() => {
        const originals = item?.exportedTimesheets?.map(item => ({
            ...item,
            type: "original",
        })) || [];

        const retros = item?.retros?.map(item => ({
            ...item,
            type: "retro",
        })) || [];

        const combined = [...originals, ...retros];
        return combined.reduce((acc, curr) => {
            const date = curr.date;

            if (!acc[date]) {
                acc[date] = {
                    original: [],
                    retro: [],
                };
            }

            if (curr.type === "original") {
                acc[date].original.push(curr);
            } else {
                acc[date].retro.push(curr);
            }

            return acc;
        }, {});
    }, [item?.exportedTimesheets, item?.retros]);

    const { diffHours, diffTotal, diffPremium } = useMemo(() => {
        const retros = item?.retros;
        const diffTotal = retros
            // ?.filter((timeSheet) => !!timeSheet?.isNonProductiveTime)
            ?.reduce((total, timeSheet) => total + timeSheet.total, 0);
        const diffHours = retros?.filter((timeSheet) => {
            if (timeSheet.hourType === "unpaid") return false;
            if (timeSheet.payCode.excludeFromPaidHours) return false;
            return true;
        }).reduce((total, timeSheet) => total + timeSheet.duration, 0);
        const diffPremium = retros?.filter((timeSheet) => {
            if (timeSheet.hourType === "unpaid") return false;
            if (timeSheet.payCode.excludeFromPaidHours) return false;
            return true;
        }).reduce((total, timeSheet) => total + timeSheet.otherDuration, 0);
        return { diffTotal, diffHours, diffPremium };
    }, [item?.retros]);

    const dateValues = useMemo(() => {
        const values = Object.keys(groupedByDate || {});
        if (item?.rowDate) {
            return values
                ?.filter((value) => moment(value).isSame(moment(item?.rowDate), "date"))
                .sort((a, b) =>
                    moment(a).valueOf() - moment(b).valueOf());
        }
        return values.sort((a, b) =>
            moment(a).valueOf() - moment(b).valueOf());
    }, [groupedByDate, item?.rowDate]);

    const table = useNewTable({
        data: [],
        totalItems: 0,
        columns,
        initialState: {
            columnVisibility: {
                "payCode_hourType": false,
                location: false,
                job: false,
            }
        }
    });

    useEffect(() => {
        if (timesheetCustomFields?.length > 0) {
            table.setColumnVisibility((prev) => {
                const columnVisibility = prev;
                timesheetCustomFields.forEach((field) => {
                    const value = `customFields_${field.code}`;
                    columnVisibility[value] = false;
                })
                return columnVisibility;
            })
        }
    }, [timesheetCustomFields, table]);

    useEffect(() => {
        if (groups?.length > 0) {
            table.setColumnVisibility((prev) => {
                const columnVisibility = prev;
                groups.forEach((field) => {
                    const value = field.level.replaceAll("_", "");
                    columnVisibility[value] = false;
                })
                return columnVisibility;
            })
        }
    }, [groups, table]);

    const isExpaned = expandedRow?.[item.id];

    return (
        <div className="border rounded-lg px-3 py-2 shadow-sm">
            <div className="d-flex justify-content-between align-items-center flex-wrap">
                <div className="d-flex align-items-center gap-x-7 py-3 px-2">
                    <div className="d-flex align-items-center gap-2 m-0 p-0 text-sm text-primary">
                        {!hideExpand && (
                            <FontAwesomeIcon
                                icon={isExpaned ? faChevronUp : faChevronDown}
                                className="mr-3"
                                onClick={() => setExpandedRow((prev) => ({ ...prev, [item.id]: !prev?.[item.id] }))}
                            />
                        )}
                        <FontAwesomeIcon icon={faClock} />
                        <p className="m-0 p-0 font-weight-bolder">
                            {moment.parseZone(item?.startDate).format(dateFormat)}
                        </p>
                        <p className="m-0 p-0 font-weight-bolder"> - </p>
                        <p className="m-0 p-0 font-weight-bolder">
                            {moment.parseZone(item?.endDate).format(dateFormat)}
                        </p>
                    </div>

                    <div className="d-flex align-items-center gap-4 m-0 p-0 text-primary pt-1 font-weight-bold">
                        <div className="d-flex gap-2">
                            <span className="m-0 p-0">{t("diff-hours")}:</span>
                            <span>{renderDurationAsFormat(diffHours, "HH:mm", true, true)}</span>
                        </div>
                        <div className="d-flex gap-2">
                            <span className="m-0 p-0">{t("diff-premium-hours")}:</span>
                            <span>{renderDurationAsFormat(diffPremium, "HH:mm", true, true)}</span>
                        </div>
                        <div className="d-flex gap-2">
                            <span className="m-0 p-0">{t("diff-amount")}:</span>
                            <span>{currencyFormatter(diffTotal, 2, primaryJob?.currency || currency)}</span>
                        </div>
                    </div>
                </div>

                <ColumnToggle columns={table.getAllColumns()} />
            </div>

            {isExpaned && (
                <>
                    <hr className="my-2" />

                    <div className="p-1" style={{ maxHeight: "calc(100% - 60px)", overflow: "auto" }}>
                        {dateValues?.map((date) => {
                            const isExpaned = expanded?.[date];
                            return (
                                <Item
                                    data={groupedByDate}
                                    date={date}
                                    isExpaned={isExpaned}
                                    columnVisibility={table.getState().columnVisibility}
                                    columns={columns}
                                    setExpanded={setExpanded}
                                    item={primaryJob}
                                />
                            )
                        })}
                    </div>
                </>
            )}
        </div>
    );
}

export default RetroContent;
